import React, { useState, useEffect } from 'react';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faEraser } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select'
import StyledSelect from '../components/StyledSelect'
import hljsLanguages from 'react-syntax-highlighter/dist/cjs/languages/hljs/supported-languages'

const langOptions = hljsLanguages.map(function(l) { return { value: l, label: l} } )

const CodeDiffChecker = (props) => {
  const { editorTheme, compareLanguage, appTheme } = props;
  const [language, setLanguage] = useState(compareLanguage || 'json')
  const [languageOption, setLanguageOption] = useState()
  const [originalInput, setOriginalInput] = useState('');
  const [compareInput, setCompareInput] = useState('');
  const [renderSideBySide, setRenderSideBySide] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      if (event.target.name === 'file1') {
        setOriginalInput(content)
      } else {
        setCompareInput(content)
      }
    };
    reader.readAsText(file);
  };

  const handleLangSelectChange = (option) => {
    setLanguageOption(option)
    setLanguage(option.value)
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 xl:gap-4">
      { !compareLanguage && (
        <div className="col-span-2">
          <div className="p-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
            <div className="flex justify-center items-center">
              <StyledSelect appTheme={appTheme} className='w-1/4' value={languageOption} options={langOptions} onChange={handleLangSelectChange} placeholder='Select Language' />
            </div>
          </div>
        </div>
      )}
      <div className="col-span-2 xl:col-auto">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>Original Input</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <Editor 
              height="40vh"
              value={originalInput}
              onChange={(value) => setOriginalInput(value)}
              language={language}
              theme={editorTheme}
              options={{ readOnly: false }}
              className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            /> 
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <input
              type="file"
              accept='.*'
              onChange={handleFileUpload}
              className='hidden'
              name='file1'
            />
            <button
              type="button"
              onClick={() => document.querySelector('input[name="file1"]').click()}
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCloudArrowUp}/>
              &nbsp; Browse your file
            </button>
            <button
              type="button"
              onClick={() => setOriginalInput('')}
              className="text-white bg-gray-400 hover:bg-gray-500 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp; Clear
            </button>
          </div>
        </div>
      </div>

      <div className="col-span-2 xl:col-auto">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-blue-600 p-2'>Compare Input</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <Editor 
              height="40vh"
              value={compareInput}
              onChange={(value) => setCompareInput(value)}
              language={language}
              theme={editorTheme}
              className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            <input
              type="file"
              accept='.*'
              onChange={handleFileUpload}
              className='hidden'
              name='file2'
            />
            <button
              type="button"
              onClick={() => document.querySelector('input[name="file2"]').click()}
              className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faCloudArrowUp}/>
              &nbsp; Browse your file
            </button>
            <button
              type="button"
              onClick={() => setCompareInput('')}
              className="text-white bg-gray-400 hover:bg-gray-500 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 mb-2"
            >
              <FontAwesomeIcon icon={faEraser}/>
              &nbsp; Clear
            </button>
          </div>
        </div>
      </div>

      {/* Diff Result Card */}
      <div className="col-span-2">
        <div className="p-4 mb-4 space-y-6 bg-white border border-gray-200 rounded-lg shadow-sm 2xl:col-span-2 dark:border-gray-700 sm:p-6 dark:bg-gray-800">
          <div className="px-4 py-2 text-gray-400 border border-gray-200 border-dashed rounded dark:border-gray-600">
            <h3 className='uppercase border-l-4 border-yellow-600 p-2'>Original Input with diff</h3>
          </div>
          <div className="px-4 py-2 text-gray-400 rounded dark:border-gray-600">
            { originalInput && compareInput && (
              <>
                <div className="flex items-center mb-4">
                  <input
                    checked={renderSideBySide}
                    onChange={() => setRenderSideBySide(!renderSideBySide)}
                    id="checked-checkbox" type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"/>
                  <label for="checked-checkbox" class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">Show diff side by side</label>
                </div>
                <DiffEditor
                  height="50vh"
                  original={originalInput}
                  modified={compareInput}
                  language={language}
                  theme={editorTheme}
                  // onMount={(editor, monaco) => {
                  //   editor.getOriginalEditor().onDidChangeModelContent((event) => {
                  //     setOriginalInput(editor.getOriginalEditor().getValue());
                  //   });
                  // }}
                  options={{
                    readOnly: true,
                    renderSideBySide: renderSideBySide,
                  }}
                  className="block p-2.5 w-full text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
              </>
            ) }
          </div>
        </div>
      </div>
    </div>
  )
}

export default CodeDiffChecker;
